// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import { apiClient } from "@/queries/client"

// Both hooks call through `apiClient` (single instance, request-interceptor auth,
// central 401 redirect — see ADR-0011). Wire is snake_case (PRD §Invariants INV-6).
// Query keys are stable arrays so views can `invalidateQueries` surgically when
// future slices mutate forge aggregate counters (`n_cycles`, `last_cycle_at`).

export type ListForgesParams = {
  q?: string
  created_after?: string
  created_before?: string
  include_all?: boolean
}

// Skips absent/empty values so the no-filter URL stays bookmarkable (ADR-0006).
const buildListParams = (params: ListForgesParams | undefined) => {
  const out: Record<string, string> = {}
  if (params?.q && params.q.trim().length > 0) out.q = params.q.trim()
  if (params?.created_after) out.created_after = params.created_after
  if (params?.created_before) out.created_before = params.created_before
  if (params?.include_all === true) out.include_all = "true"
  return out
}

// `next_cursor` is always null in v1 — cursor pagination is a placeholder (PRD §Out of Scope).
// QueryKey embeds params so filtered and unfiltered cache entries coexist independently.
export const useForges = (
  params?: ListForgesParams,
): UseQueryResult<ForgeListResponse, Error> => {
  const queryParams = buildListParams(params)
  return useQuery<ForgeListResponse, Error>({
    queryKey: ["forges", "list", queryParams],
    staleTime: 1000 * 60,
    queryFn: async () => {
      const response = await apiClient.get<ForgeListResponse>(
        "/front-api/forges",
        { params: queryParams },
      )
      return response.data
    },
  })
}

// Disabled when `forgeId` is undefined — `useParams` can return undefined briefly
// before the URL param resolves, so this prevents a spurious network request.
export const useForge = (
  forgeId: string | undefined,
): UseQueryResult<ForgeDetails, Error> => {
  return useQuery<ForgeDetails, Error>({
    queryKey: ["forges", "detail", forgeId],
    enabled: typeof forgeId === "string" && forgeId.length > 0,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const response = await apiClient.get<ForgeDetails>(
        `/front-api/forges/${encodeURIComponent(forgeId as string)}`,
      )
      return response.data
    },
  })
}

// Server owns the ordering (direction-aware) — returns the envelope verbatim,
// never re-sorting client-side. `top` is omitted when undefined so the URL
// stays clean for the Backend default.
export const useForgeArena = (
  forgeId: string | undefined,
  top?: number,
): UseQueryResult<ForgeArenaResponse, Error> => {
  return useQuery<ForgeArenaResponse, Error>({
    queryKey: ["forges", "arena", forgeId, top ?? null],
    enabled: typeof forgeId === "string" && forgeId.length > 0,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const response = await apiClient.get<ForgeArenaResponse>(
        `/front-api/forges/${encodeURIComponent(forgeId as string)}/arena`,
        { params: top === undefined ? {} : { top } },
      )
      return response.data
    },
  })
}
